// Note: This hook uses web-only APIs (localStorage, window).
// For cross-platform support, refactor browser-specific logic into injected callbacks.

import { useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';

function isTokenExpired() {
  const token = localStorage.getItem('token');
  if (!token) return true;

  try {
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch (err) {
    return true;
  }
}

// Public routes that should NOT be forced to /login when unauthenticated
const PUBLIC_PATHS = new Set([
  '/',
  '/privacy',
  '/terms',
  '/sms-optin',
  '/sms-signup',
  '/registration-complete',
  '/about',
  '/login',
  '/create-account',
  '/verify-success',
  '/verify-failed',
  '/forgot-password',
  '/email-not-verified',
  '/auth/2fa', // 2FA challenge uses a short-lived challenge token, not the normal session token
  '/reset-password',
  '/forgo-password',
]);

function isPublicPath(pathname) {
  // exact matches
  if (PUBLIC_PATHS.has(pathname)) return true;

  // If you later add routes like /verify-success/something, allow prefixes here.
  // Example:
  // if (pathname.startsWith('/verify-')) return true;

  return false;
}

export default function useAuthTokenChecker() {
  useEffect(() => {
    const checkToken = () => {
      const pathname = window.location.pathname;

      // ✅ Never redirect public pages
      if (isPublicPath(pathname)) return;

      if (isTokenExpired()) {
        // Clear auth/session state and force login
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch {
          // ignore
        }

        window.location.replace('/login');
      }
    };

    checkToken(); // on mount
    const interval = setInterval(checkToken, 60 * 1000); // every 1 minute

    return () => clearInterval(interval);
  }, []);
}
